import type { AppConfig } from "../types";

/** Cliente do backend SupriAI (launcher.supricrm.tech). */

export interface Conversation {
  id: string;
  agent_id: string;
  title: string;
  updated_at: string;
  created_at: string;
  quote_id?: number | null;
}

export interface Message {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content?: string | null;
  tool_calls?: any;
  tool_call_id?: string | null;
  name?: string | null;
  model?: string | null;
  tokens_in?: number | null;
  tokens_out?: number | null;
  created_at: string;
}

export interface ChatResponse {
  conversation_id: string;
  final_text: string;
  rounds: { turn: number; tool_calls?: string[]; final?: boolean }[];
  error?: string;
}

function authHeaders(cfg: AppConfig): Record<string, string> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (cfg.apiKey) h["Authorization"] = `Bearer ${cfg.apiKey}`;
  return h;
}

function url(cfg: AppConfig, path: string): string {
  const base = (cfg.apiUrl || "").replace(/\/$/, "");
  return `${base}${path}`;
}

export async function login(cfg: AppConfig, email: string, password: string): Promise<{ access_token: string; email: string }> {
  const r = await fetch(url(cfg, "/api/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!r.ok) throw new Error(`login ${r.status}: ${await r.text().catch(() => "")}`);
  return r.json();
}

export async function whoami(cfg: AppConfig): Promise<{ email: string }> {
  const r = await fetch(url(cfg, "/api/auth/me"), { headers: authHeaders(cfg) });
  if (!r.ok) throw new Error(`me ${r.status}`);
  return r.json();
}

export async function meta(cfg: AppConfig): Promise<any> {
  const r = await fetch(url(cfg, "/api/meta"), { headers: authHeaders(cfg) });
  if (!r.ok) throw new Error(`meta ${r.status}`);
  return r.json();
}

export async function chat(cfg: AppConfig, body: {
  agent: string;
  message: string;
  conversation_id?: string | null;
  provider?: string;
  model?: string;
}): Promise<ChatResponse> {
  const r = await fetch(url(cfg, "/api/chat"), {
    method: "POST",
    headers: authHeaders(cfg),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`chat ${r.status}: ${await r.text().catch(() => "")}`);
  return r.json();
}

export async function listConversations(cfg: AppConfig): Promise<{ items: Conversation[] }> {
  const r = await fetch(url(cfg, "/api/conversations"), { headers: authHeaders(cfg) });
  if (!r.ok) throw new Error(`list ${r.status}`);
  return r.json();
}

export async function getConversation(cfg: AppConfig, id: string): Promise<{ conversation: Conversation; messages: Message[] }> {
  const r = await fetch(url(cfg, `/api/conversations/${id}`), { headers: authHeaders(cfg) });
  if (!r.ok) throw new Error(`get ${r.status}`);
  return r.json();
}

export async function deleteConversation(cfg: AppConfig, id: string): Promise<void> {
  const r = await fetch(url(cfg, `/api/conversations/${id}`), { method: "DELETE", headers: authHeaders(cfg) });
  if (!r.ok) throw new Error(`del ${r.status}`);
}

export async function getAudit(cfg: AppConfig): Promise<{ items: any[] }> {
  const r = await fetch(url(cfg, "/api/audit"), { headers: authHeaders(cfg) });
  if (!r.ok) throw new Error(`audit ${r.status}`);
  return r.json();
}

export async function pingProvider(cfg: AppConfig): Promise<{ ok: boolean; detail: string }> {
  try {
    const r = await fetch(url(cfg, "/api/health"));
    return { ok: r.ok, detail: `HTTP ${r.status}` };
  } catch (e: any) {
    return { ok: false, detail: e.message || "rede" };
  }
}
